import { ComponentPortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { CalendarUserEvent } from './calendar-body.component';
import { SimCalendarComponent, SimCalendarView } from './calendar.component';
import { SimDateRangeInputComponent } from './date-range-input.component';
import { SimEndDateDirective, SimStartDateDirective } from './date-range-input.directive';
import { DateRange, ExtractDateTypeFromSelection, SIM_RANGE_DATE_SELECTION_MODEL_PROVIDER } from './date-selection-model';
import { simDatePickerAnimations } from './datepicker-animations';
import { SimDatepickerBase, SimDatepickerControl } from './datepicker-base';
import { SimDatepickerContentBase } from './datepicker-content-base';

export interface SimDateRangePickerInput<D> extends SimDatepickerControl<D> {
  comparisonStart: D | null;
  comparisonEnd: D | null;
}

@Component({
  selector: 'sim-date-range-picker-content',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  animations: [simDatePickerAnimations.transformPanel, simDatePickerAnimations.fadeInCalendar],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sim-datepicker-content sim-date-range-picker-content'
  }
})
export class SimDateRangePickerContentComponent<S, D = ExtractDateTypeFromSelection<S>> extends SimDatepickerContentBase<S, D>
  implements OnInit, AfterViewInit {
  @ViewChildren(SimCalendarComponent) _calendars: QueryList<SimCalendarComponent<D>>;

  enableStartPrev: boolean = true;
  enableStartNext: boolean = false;
  enableEndPrev: boolean = false;
  enableEndNext: boolean = true;

  _startAt: D;
  _endAt: D;

  rangeComparisonStart: D;

  rangeComparisonEnd: D;

  _focusedInput: SimStartDateDirective<D> | SimEndDateDirective<D>;

  ngOnInit(): void {
    const { start, end } = this._getStartAt();
    this._startAt = start;
    this._endAt = end;
  }

  ngAfterViewInit(): void {
    this._calendar = this._calendars && this._calendars.first;
    super.ngAfterViewInit();
    this._focusedInput = this.datepicker._datepickerInput.focusedInput;
  }

  _getSelected(): DateRange<D> | null {
    return this._model ? ((this._model.selection as unknown) as DateRange<D>) : null;
  }

  _handleUserSelection(event: CalendarUserEvent<D | null>, range?: 'start' | 'end'): void {
    if (this._model && this._dateAdapter) {
      const selection = this._model.selection;
      const value = event.value;
      const isRange = selection instanceof DateRange;
      if (isRange && this._rangeSelectionStrategy) {
        const currentRange = (selection as unknown) as DateRange<D>;
        if (this._focusedInput instanceof SimStartDateDirective) {
          // 如果有结束
          // 选择的大于结束时间，清空结束时间
          // 选择的小于结束时间，直接完成
          const newSelection = this._rangeSelectionStrategy.selectionFinished(value, currentRange, event.event);
          console.log('_handleUserSelection', range, 'currentRange：', currentRange, 'value', value, 'newSelection', newSelection);
          this._model.updateSelection((newSelection as unknown) as S, this);
        } else if (this._focusedInput instanceof SimEndDateDirective) {
          // 如果有开始
          // 选择的小于开始时间，清空结束时间
          // 选择的大于开始时间，直接完成
          let newSelection: DateRange<D>;
          console.log('currentRange', currentRange);
          if ((!currentRange.start && !currentRange.end) || !currentRange.end) {
            newSelection = this._rangeSelectionStrategy.selectionFinished(value, currentRange, event.event);
          } else if (!currentRange.start) {
            if (value > currentRange.end) {
              newSelection = new DateRange(value, null);
            } else {
              newSelection = new DateRange(value, currentRange.end);
            }
          } else {
            newSelection = new DateRange(null, value);
          }

          console.log('_handleUserSelection', range, 'currentRange：', currentRange, 'value', value, 'newSelection', newSelection);
          this._model.updateSelection((newSelection as unknown) as S, this);
        }
      } else if (value && (isRange || !this._dateAdapter.sameDate(value, (selection as unknown) as D))) {
        this._model.add(value);
      }
    }
    if (!this._model || this._model.isComplete()) {
      this.datepicker.close();
    }
  }

  _setStartAt($event: [D, SimCalendarView, number], dir: 'start' | 'end') {
    const [date, view, num] = $event;
    console.log('_setStartAt', view);
    switch (view) {
      case 'month':
        if (dir === 'start') {
          this.enableStartNext = false;
          this._calendars.first.activeDate = date;
          this._calendars.last.activeDate = this._dateAdapter.addMonths(date, -num);
        } else if (dir === 'end') {
          this.enableEndPrev = false;
          this._calendars.first.activeDate = this._dateAdapter.addMonths(date, -num);
          this._calendars.last.activeDate = date;
        }
        break;
      case 'year':
      case 'multi-year':
        if (dir === 'start') {
          this.enableStartNext = true;
        } else if (dir === 'end') {
          this.enableEndPrev = true;
        }
        break;
      default:
        break;
    }
  }

  private _getStartAt() {
    const _dateAdapter = this._dateAdapter;
    const today = _dateAdapter.today();
    let { start, end } = this._getSelected() || {};
    // 如果 start 和 end 同年同月 => start 大于本月 end 小于本月 start
    // console.log('_getStartAt', start, end, today);
    if (!start && !end) {
      // console.log('_getStartAt start and end is null');
      start = this.datepicker.startAt || today;
      end = _dateAdapter.addMonths(start, 1);
    } else if (start && end) {
      // console.log('_getStartAt 有值', start, end);
      if (_dateAdapter.getYear(start) === _dateAdapter.getYear(end) && _dateAdapter.getMonth(start) === _dateAdapter.getMonth(end)) {
        // console.log('_getStartAt 同年同月', start, end);
        if (_dateAdapter.getYear(start) === _dateAdapter.getYear(today) && _dateAdapter.getMonth(start) > _dateAdapter.getMonth(today)) {
          end = start;
          start = _dateAdapter.addMonths(start, -1);
        } else {
          end = _dateAdapter.addMonths(start, 1);
        }
      } else {
        // console.log('_getStartAt 不是同月', start, end);
      }
    }
    return {
      start,
      end
    };
  }
}

@Component({
  selector: 'sim-date-range-picker',
  template: '',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SIM_RANGE_DATE_SELECTION_MODEL_PROVIDER],
  exportAs: 'simDateRangePicker'
})
export class SimDateRangePickerComponent<D> extends SimDatepickerBase<SimDateRangeInputComponent<D>, DateRange<D>, D> {
  simCalendarFooter: TemplateRef<void>;
  protected createPortal() {
    return new ComponentPortal<SimDateRangePickerContentComponent<D, D>>(SimDateRangePickerContentComponent, this._viewContainerRef);
  }
}
